/**
 * Format date to dd/mm/yyyy format
 * @param {Date|string} date - Date object or date string
 * @returns {string} Formatted date string in dd/mm/yyyy format
 */
export const formatDateDDMMYYYY = (date) => {
    if (!date) return 'N/A';
    
    try {
        const dateObj = date instanceof Date ? date : new Date(date);
        
        // Check if date is valid
        if (isNaN(dateObj.getTime())) {
            return 'N/A';
        }
        
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();
        
        return `${day}/${month}/${year}`;
    } catch (error) {
        console.error('Error formatting date:', error);
        return 'N/A';
    }
};

/**
 * Format date and time to dd/mm/yyyy HH:mm format
 * @param {Date|string} date - Date object or date string
 * @returns {string} Formatted date and time string
 */
export const formatDateTimeDDMMYYYY = (date) => {
    if (!date) return 'N/A';
    
    try {
        const dateObj = date instanceof Date ? date : new Date(date);
        
        // Check if date is valid
        if (isNaN(dateObj.getTime())) {
            return 'N/A';
        }
        
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();
        const hours = String(dateObj.getHours()).padStart(2, '0');
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
        console.error('Error formatting date time:', error);
        return 'N/A';
    }
};

/**
 * Format date and time to dd/mm/yyyy HH:mm:ss format
 * @param {Date|string} date - Date object or date string
 * @returns {string} Formatted date and time string with seconds
 */
export const formatDateTimeFullDDMMYYYY = (date) => {
    if (!date) return 'N/A';
    
    try {
        const dateObj = date instanceof Date ? date : new Date(date);
        
        // Check if date is valid
        if (isNaN(dateObj.getTime())) {
            return 'N/A';
        }
        
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();
        const hours = String(dateObj.getHours()).padStart(2, '0');
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        const seconds = String(dateObj.getSeconds()).padStart(2, '0');
        
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
        console.error('Error formatting date time:', error);
        return 'N/A';
    }
};

/**
 * Safely parse date strings from API response, handling custom format:
 * "D/M/YYYY, h:mm:ss am/pm" or similar
 */
const parseDateSafely = (date) => {
    if (!date) return null;
    if (date instanceof Date) return date;

    if (typeof date === 'string') {
        const match = date.trim().match(/^(\d+)\/(\d+)\/(\d+)(?:,\s*|\s+)(\d+):(\d+):(\d+)\s*(am|pm|AM|PM)$/i);
        if (match) {
            const [_, dayStr, monthStr, yearStr, hourStr, minStr, secStr, ampmStr] = match;
            const day = parseInt(dayStr, 10);
            const month = parseInt(monthStr, 10) - 1; // 0-based
            const year = parseInt(yearStr, 10);
            let hours = parseInt(hourStr, 10);
            const minutes = parseInt(minStr, 10);
            const seconds = parseInt(secStr, 10);
            const ampm = ampmStr.toLowerCase();

            if (ampm === 'pm' && hours < 12) {
                hours += 12;
            } else if (ampm === 'am' && hours === 12) {
                hours = 0;
            }

            const parsed = new Date(year, month, day, hours, minutes, seconds);
            if (!isNaN(parsed.getTime())) {
                return parsed;
            }
        }
    }

    const fallback = new Date(date);
    return isNaN(fallback.getTime()) ? null : fallback;
};

/**
 * Format date and time to dd/mm/yyyy hh:mm AM/PM format (12-hour format)
 * @param {Date|string} date - Date object or date string
 * @returns {string} Formatted date and time string with 12-hour format and AM/PM
 */
export const formatDateTimeDDMMYYYY12Hour = (date) => {
    if (!date) return 'N/A';
    
    try {
        const dateObj = parseDateSafely(date);
        
        // Check if date is valid
        if (!dateObj) {
            return 'N/A';
        }
        
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();
        
        let hours = dateObj.getHours();
        const minutes = dateObj.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'
        const hoursStr = String(hours).padStart(2, '0');
        const minutesStr = String(minutes).padStart(2, '0');
        
        return `${day}/${month}/${year} ${hoursStr}:${minutesStr} ${ampm}`;
    } catch (error) {
        console.error('Error formatting date time:', error);
        return 'N/A';
    }
};

/**
 * Format date and time to dd/mm/yyyy hh:mm:ss AM/PM format (12-hour format with seconds)
 * @param {Date|string} date - Date object or date string
 * @returns {string} Formatted date and time string with seconds in 12-hour format and AM/PM
 */
export const formatDateTimeFullDDMMYYYY12Hour = (date) => {
    if (!date) return 'N/A';
    
    try {
        const dateObj = date instanceof Date ? date : new Date(date);
        
        // Check if date is valid
        if (isNaN(dateObj.getTime())) {
            return 'N/A';
        }
        
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();
        
        let hours = dateObj.getHours();
        const minutes = dateObj.getMinutes();
        const seconds = dateObj.getSeconds();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'
        const hoursStr = String(hours).padStart(2, '0');
        const minutesStr = String(minutes).padStart(2, '0');
        const secondsStr = String(seconds).padStart(2, '0');
        
        return `${day}/${month}/${year} ${hoursStr}:${minutesStr}:${secondsStr} ${ampm}`;
    } catch (error) {
        console.error('Error formatting date time:', error);
        return 'N/A';
    }
};

